import { Container } from 'Components/Container';
import { useNavigate } from 'react-router-dom';
import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { deleteGapAnalysisVinculo } from '../../../services/gap-analysis';
import { getComplianceProgramDetails } from '../../../services/complice-program'
import { PageLoaderShow } from '../../../Components/PageLoader/Show';
import { ExpandableTable } from '../../../Components/ExpandableTable';
import { tableHeader } from './table-header';
import { tableEntity } from './table-entity';
import { Information } from '../../../Components/Information';
import { StatusProgressBar  } from '../../../Components/StatusProgressBar';
import { Card } from '../../../Components/Card';
import { Alert, Box, Link, Snackbar } from '@mui/material';
import { Title } from '../../GapAnalysis/Show/ControlDetal/styles';
import { ButtonAdd } from '../../../Components/Container/styles';
import { Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import { Column, Row } from '../../../Components/Grid';
import { ModalConfirmDelete } from '../../../Components/ModalConfirmDelete';
import { typeLabels, statusControl, maturityLevels } from '../../../utils/labels_utils'
import { useSelector } from 'react-redux';
import { useAuthTenant } from '../../../store/auth';
import { PageHelper } from '../../../Components/PageHelper';


const descriptionContent = (
  <div style={{ marginTop: "10px", display: "flex", flexDirection: "column", gap: "4px" }}>
    {Object.entries(statusControl).map(([key, { label, color, description }]) => (
      <div key={key} style={{ display: "flex", alignItems: "center", gap: "4px" }}>
        <div style={{ width: "16px", height: "16px", backgroundColor: color, borderRadius: "4px" }}></div>
        <strong>{label}</strong> - {description}
      </div>
    ))}
  </div>
);

const maturityLevelsOptions = maturityLevels;

// Tour steps
const tourSteps = [
  {
    target: 'body',
    content: 'Bem-vindo à página de detalhes do Programa de Privacidade e Proteção de Dados! Aqui você pode visualizar o status de implementação dos controles, vincular entidades e filtrar informações.',
    placement: 'center',
    disableBeacon: true,
  },
  {
    target: '[data-tour="page-title"]',
    content: 'Clique aqui no título da página para voltar à lista de Programas de Compliance.',
    disableScrolling: true,
  },
  {
    target: '[data-tour="vincular-button"]',
    content: 'Use este botão para vincular controles às entidades da sua empresa (Áreas, Ativos, Dados, Processos e Sistemas).',
    disableScrolling: true,
  },
  {
    target: '[data-tour="status-bar"]',
    content: 'Esta barra mostra o progresso de implementação dos controles, dividido por status de conformidade.',
    disableScrolling: true,
  },
  {
    target: '[data-tour="status-info"]',
    content: 'Aqui você encontra a descrição detalhada de cada status de implementação e sua respectiva cor na legenda.',
    disableScrolling: true,
  },
  {
    target: '[data-tour="filters"]',
    content: 'Utilize estes filtros avançados para refinar a visualização dos controles por: Domínio, Vínculo, Status, Nome da Entidade, Tipo de Entidade, Nível de Maturidade e Peso do Controle.',
  },
  {
    target: '[data-tour="controls-table"]',
    content: 'Esta tabela expansível mostra todos os controles do programa. Clique nas linhas para expandir e visualizar as entidades vinculadas a cada controle.',
  },
  {
    target: 'body',
    content: 'Dica: Você pode desvincular entidades específicas clicando no ícone de lixeira ao lado de cada entidade na tabela expandida.',
    placement: 'center',
  },
  {
    target: 'body',
    content: 'Tour concluído! Explore a página e use os filtros para encontrar exatamente o que você precisa.',
    placement: 'center',
  },
];

export const ComplianceProgramShow = () => {
  const navigate = useNavigate();
  const selectedTenant = useSelector(useAuthTenant);

  const [itemToDelete, setItemToDelete] = useState(null);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [filters, setFilters] = useState({
    peso: '',
    dominio: '',
    vinculo: '',
    entidadeTipo: '',
    entidadeNome: '',
    status: '',
  });

  const {
    isLoading,
    error,
    refetch,
    data: gapAnalysis,
  } = useQuery('getComplianceProgramDetails', async () => {
    try {
      const data = await getComplianceProgramDetails(selectedTenant._id);
      return data.status === 200 ? data.data : null;
    } catch (err) {
      if (err?.response?.status === 404) {
        return { hasComplianceProgram: false };
      }
      throw err;
    }
  });

  if (isLoading) return <p>Carregando...</p>;
  if (error) return <p>Erro ao carregar os dados.</p>;

  if (gapAnalysis?.hasComplianceProgram === false) {
    return (
      <Information
        title="Programa de Privacidade e Proteção de Dados Não Encontrado"
        description={
          <>
            <div>Esta empresa ainda não possui um Programa de Privacidade e Proteção de Dados cadastrado.</div>
            <div>
              <Link
                href="/compliance-program/add"
                style={{ color: '#007bff', textDecoration: 'underline' }}
              >
                Clique aqui para realizar o cadastro.
              </Link>
            </div>
          </>
        }
        style={{
          marginTop: '10px',
          marginBottom: '10px',
          backgroundColor: '#fff3cd',
          border: '1px solid #ffeeba',
          borderRadius: '8px',
          padding: '16px',
          color: '#856404',
        }}
      />
    );
  }


  const id = gapAnalysis.gapAnalysis?._id;

  const formattedStatuses = Object.keys(statusControl).map(status => ({
    label: statusControl[status].label,
    count: gapAnalysis.statusCounts[status] || 0,
    color: statusControl[status].color,
  }));


  const uniqueValues = (key: string) => [...new Set(gapAnalysis.controls.map(control => control[key]))].sort();

  const getUniqueEntities = () => {
    const entityMap = new Map();
    const entityTypes = new Set();
    const entityStatuses = new Set();
    const maturityLevels = new Set();

    gapAnalysis.controls.forEach(control => {
      control.entities.forEach(entity => {
        const key = `${entity.entity_id}-${entity.entity_type}`;

        if (!entityMap.has(key)) {
          entityMap.set(key, {
            entity_id: entity.entity_id,
            entity_name: entity.entity_name,
          });
        }

        entityTypes.add(entity.entity_type);
        entityStatuses.add(entity.status);
        maturityLevels.add(entity.maturity_level);

      });
    });

    return {
      entities: Array.from(entityMap.values()),
      entityTypes: Array.from(entityTypes),
      entityStatuses: Array.from(entityStatuses),
      maturityLevels: Array.from(maturityLevels),

    };
  };

  const { entities, entityTypes, entityStatuses, maturityLevels  } = getUniqueEntities();

  const handleFilterChange = (field: string, value: string) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };
  const filteredControls = gapAnalysis.controls
    .map(control => {
      const filteredEntities = filters.entidadeNome || filters.entidadeTipo || filters.maturityLevel || filters.status
        ? control.entities.filter(entity => (
          (filters.entidadeTipo ? entity.entity_type === filters.entidadeTipo : true) &&
          (filters.entidadeNome ? entity.entity_id === filters.entidadeNome : true) &&
          (filters.maturityLevel ? entity.maturity_level === filters.maturityLevel : true) &&
          (filters.status ? entity.status === filters.status : true)
        ))
        : control.entities;

      return {
        ...control,
        entities: filteredEntities,
      };
    })
    .filter(control =>
      (control.entities.length > 0 || !(filters.entidadeNome || filters.entidadeTipo || filters.maturityLevel || filters.status)) &&
      (filters.peso ? parseInt(control.controlWeighting, 10) === parseInt(filters.peso, 10) : true) &&
      (filters.dominio ? control.scfDomain === filters.dominio : true) &&
      (filters.vinculo ? control.vinculado.toString() === filters.vinculo : true)
    );

  const link_vinculo = `/compliance-program/show/${id}`;

  const button = (
    <div data-tour="vincular-button">
      <ButtonAdd
        label="+ Vincular Controles"
        className="no-margin"
        onClick={() => navigate(`${link_vinculo}/vincular`)}
      />
    </div>
  );

  const link_ = `compliance-program/${id}`;

  const onDelete = item => {
    setItemToDelete(item);
    setOpenDeleteModal(true);
  };

  const onCloseDeleteModal = () => {
    setOpenDeleteModal(false);
    setItemToDelete(null);
  };
  const callDeleteAction = async (itemToDelete, onClose, refetch, setSnackbar,) => {
    try {
      await deleteGapAnalysisVinculo(itemToDelete._id);
      setSnackbar({ open: true, message: "Vínculo removido com sucesso!", severity: "success" });
      refetch();
      onClose();
    } catch (error) {
      setSnackbar({ open: true, message: "Erro ao remover o vínculo.", severity: "error" });
    }
  };


  const onConfirmDeleteModal = async () => {
    try {
      await callDeleteAction(itemToDelete, onCloseDeleteModal, refetch, setSnackbar);
    } catch (error) {
      onCloseDeleteModal();

      setSnackbar({
        open: true,
        message: error.response?.data?.message ?? "Erro ao remover o vínculo.",
        severity: "error",
      });
    }
  };


  return (
    <>
      {!isLoading && !error && (
        <>
        {/* Helper Interativo */}
        <PageHelper 
          steps={tourSteps}
          tourKey="compliance-program-show-tour"
          autoStart={true}
          showButton={true}
        />
        <Container title={`${gapAnalysis.gapAnalysis?.name}`}
                   breadcrumb={`Gerenciar / ${gapAnalysis.gapAnalysis?.name}`}
                   actionButton={button}
        >
          <Box sx={{ marginBottom: '10px', marginTop: '10px' }} data-tour="status-bar">
            <Card>
              <Title className="m-0 mb-2">Status de Implementação</Title>
              <StatusProgressBar statuses={formattedStatuses} />
            </Card>
          </Box>

          <div data-tour="status-info">
            <Information
              title={'Descrição dos Status de Implementação'}
              description={descriptionContent}
              style={{
                marginTop: '10px',
                marginBottom: '10px',
              }}
            />
          </div>


          <Card className="mb-2 mt-3" data-tour="filters">
            <Row className="mb-3" md={'3'}>
              <Column xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel id="dominio-label" shrink>Domínio</InputLabel>
                  <Select
                    labelId="dominio-label"
                    variant="outlined"
                    label="Domínio"
                    notched
                    sx={{
                      minHeight: '40px',
                      maxHeight: '40px',
                      margin: '0 auto',
                      width: '100%',
                    }}
                    value={filters.dominio} onChange={(e) => handleFilterChange("dominio", e.target.value)}>
                    <MenuItem value="">Todos</MenuItem>
                    {uniqueValues('scfDomain').map(value => (
                      <MenuItem key={value} value={value}>{value}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Column>
              <Column xs={12} md={2}>
                <FormControl fullWidth>
                  <InputLabel id="vinculo-label" shrink>Vínculo</InputLabel>
                  <Select
                    labelId="vinculo-label"
                    variant="outlined"
                    label="Vínculo"
                    notched
                    sx={{
                      minHeight: '40px',
                      maxHeight: '40px',
                      margin: '0 auto',
                      width: '100%',
                    }}
                    value={filters.vinculo} onChange={(e) => handleFilterChange("vinculo", e.target.value)}>
                    <MenuItem value="">Todos</MenuItem>
                    <MenuItem value="true">Sim</MenuItem>
                    <MenuItem value="false">Não</MenuItem>
                  </Select>
                </FormControl>
              </Column>
              <Column xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel  id="status-label" shrink>Status</InputLabel>
                  <Select
                    labelId="status-label"
                    variant="outlined"
                    label="Status"
                    notched
                    sx={{
                      minHeight: '40px',
                      maxHeight: '40px',
                      margin: '0 auto',
                      width: '100%',
                    }}
                    value={filters.status} onChange={(e) => handleFilterChange("status", e.target.value)}>
                    <MenuItem value="">Todos</MenuItem>
                    {entityStatuses.map(status => (
                      <MenuItem key={status} value={status}>{statusControl[status].label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Column>
            </Row>
            <Row className="mb-3" md={'3'}>
              <Column xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel  id="name-entity-label" shrink >Nome da Entidade</InputLabel>
                  <Select
                    labelId="name-entity-label"
                    label="Nome da Entidade"
                    variant="outlined"
                    notched
                    sx={{
                      minHeight: '40px',
                      maxHeight: '40px',
                      margin: '0 auto',
                      width: '100%',
                    }}
                    value={filters.entidadeNome} onChange={(e) => handleFilterChange("entidadeNome", e.target.value)}>
                    <MenuItem value="">Todas</MenuItem>
                    {entities.map((entity) => (
                      <MenuItem key={entity.entity_id} value={entity.entity_id}>
                        {entity.entity_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Column>
              <Column xs={12} md={2}>
                <FormControl fullWidth>
                  <InputLabel  id="type-entity-label" shrink >Tipo de Entidade</InputLabel>
                  <Select
                    labelId="type-entity-label"
                    label="Tipo de Entidade"
                    variant="outlined"
                    notched
                    sx={{
                      minHeight: '40px',
                      maxHeight: '40px',
                      margin: '0 auto',
                      width: '100%',
                    }}
                    value={filters.entidadeTipo} onChange={(e) => handleFilterChange("entidadeTipo", e.target.value)}>
                    <MenuItem value="">Todos</MenuItem>
                    {entityTypes.map(type => (
                      <MenuItem key={type} value={type}>{typeLabels[type]}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Column>
              <Column xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel id="maturity-type-label" shrink >Nível de Maturidade</InputLabel>
                  <Select
                    labelId="maturity-type-label"
                    label="Nível de Maturidade"
                    notched
                    variant="outlined"
                    sx={{
                      minHeight: '40px',
                      maxHeight: '40px',
                      margin: '0 auto',
                      width: '100%',
                    }}
                    value={filters.maturityLevel} onChange={(e) => handleFilterChange("maturityLevel", e.target.value)}>
                    <MenuItem value="">Todos</MenuItem>
                    {maturityLevels.map(level => (
                      <MenuItem key={level} value={level}>{maturityLevelsOptions[level]}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Column>
              <Column xs={12} md={2}>
                <FormControl fullWidth>
                  <InputLabel id="peso-label"  shrink>Peso do Controle</InputLabel>
                  <Select
                    labelId="peso-label"
                    label="Peso do Controle"
                    notched
                    sx={{
                      minHeight: '40px',
                      maxHeight: '40px',
                      margin: '0 auto',
                      width: '100%',
                    }}
                    variant="outlined"
                    value={filters.peso} onChange={(e) => handleFilterChange("peso", e.target.value)}
                  >
                    <MenuItem value="">Todos</MenuItem>
                    {uniqueValues('controlWeighting').map(value => (
                      <MenuItem key={value} value={value}>{value}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Column>
            </Row>
          </Card>
          <div data-tour="controls-table">
            <ExpandableTable
              rows={filteredControls}
              columns={tableHeader}
              link={link_}
              linkExtend="compliance-program/control"
              entityColumns={tableEntity}
              enableSearch={true}
              enableSorting={true}
              title="Controles"
              onClickDelete={onDelete}
            />
          </div>

          <ModalConfirmDelete
            description={
              <>
                Tem certeza que deseja desvincular a entidade <strong>{` ${itemToDelete?.entity_name} `}</strong> deste controle?
              </>
            }
            open={openDeleteModal}
            onClose={onCloseDeleteModal}
            onConfirm={onConfirmDeleteModal}
          />
          <Snackbar
            open={snackbar.open}
            autoHideDuration={3000}
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
          >
            <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
              {snackbar.message}
            </Alert>
          </Snackbar>
        </Container>
        </>
      )}
      {isLoading && <PageLoaderShow />}
      {error && <div>Erro</div>}
    </>
  );
};
